import React, { memo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import Button from '@mui/material/Button';
import { IconButton, TextField } from '@mui/material';
import { Helmet } from 'react-helmet';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-hot-toast';
import style from './LoginMethods.module.scss';
import { auth, database } from '../../../helper/firebase.helper';
import googleIcon from '../../../assets/google-icon.svg';
import Loader from '../../../components/loader/Loader';
import LoginMail from '../login-mail/LoginMail';
import {
  GoogleAuthProvider,
  browserPopupRedirectResolver,
  signInWithPopup,
  getIdTokenResult
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { checkUserExists, registerClient } from '../../../helper/api.helper';
import { accountStateEnum } from '../../../helper/constants.helper';
import { getOrgCode } from '../../../helper/utils.helper';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const stepEnum = {
  EMAIL: 'email',
  SIGN_IN: 'sign-in'
};

// eslint-disable-next-line react/display-name
const LoginMethods = memo(() => {
  const setUser = useStoreActions((actions) => actions.setUser);
  const location = useLocation();
  const prefilledEmail = location.state?.email || '';
  const [isLoading, setLoading] = useState(false);
  const [step, setStep] = useState(prefilledEmail ? stepEnum.SIGN_IN : stepEnum.EMAIL);
  const [email, setEmail] = useState(prefilledEmail);
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();
  const redirectUrl = new URLSearchParams(location.search).get('redirectUrl');

  const validateUser = useCallback(
    async (res) => {
      await getIdTokenResult(res.user, true).then(async (idTokenResult) => {
        if (idTokenResult.claims.email_verified && idTokenResult.claims.userType === 0) {
          setUser(res.user);
          toast('Logged in');
          if (redirectUrl) {
            navigate(redirectUrl);
          } else {
            location.state && location.state.from ? navigate(location.state.from) : navigate('/');
          }
        } else if (idTokenResult.claims.email_verified && idTokenResult.claims.userType === 1) {
          const clientData = await getDoc(doc(database, 'clients', res.user.uid));
          if (clientData.exists()) {
            setUser(res.user);
            toast('Logged in');
            if (redirectUrl) {
              navigate(redirectUrl);
            } else {
              location.state && location.state.from ? navigate(location.state.from) : navigate('/');
            }
          } else {
            toast('Logged in as practitioner. Please sign up as client to login.');
            auth.signOut();
          }
        } else {
          toast.error('User does not exist. Please click Sign up to create an account.');
          auth.signOut();
        }
      });
    },
    [location, navigate, redirectUrl, setUser]
  );

  const registerGoogleUser = useCallback(
    async (user) => {
      navigate('/register/register-number');
      const res = await registerClient({
        email: user.email,
        name: user.displayName,
        orgCode: getOrgCode()
      })
        .then((r) => r.json())
        .catch(() => null);
      if (!res || res.status !== 'success') {
        toast.error(res?.message || 'Failed to complete sign up. Please try again.');
        await auth.signOut();
        navigate('/login');
        return true;
      }
      await auth.currentUser.getIdTokenResult(true);
      setUser(auth.currentUser);
      return true;
    },
    [navigate, setUser]
  );

  const signInGmail = async () => {
    setLoading(true);
    localStorage.setItem('isLoggingIn', true);
    const googleProvider = new GoogleAuthProvider();
    googleProvider.setCustomParameters({
      prompt: 'select_account'
    });
    let navigated = false;
    await signInWithPopup(auth, googleProvider, browserPopupRedirectResolver)
      .then(async (res) => {
        const idTokenResult = await getIdTokenResult(res.user, true);
        if (idTokenResult.claims.userType === undefined) {
          navigated = await registerGoogleUser(res.user);
          return;
        }
        await validateUser(res);
      })
      .catch((err) => console.log(err));
    if (!navigated) {
      setLoading(false);
    }
  };

  const continueWithEmail = async (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!emailRegex.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setLoading(true);
    try {
      const res = await checkUserExists({ email: trimmedEmail }).then((r) => r.json());
      if (res.accountState === accountStateEnum.NONE) {
        navigate('/register/register-user', { state: { email: trimmedEmail } });
      } else {
        setStep(stepEnum.SIGN_IN);
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const editEmail = () => {
    setStep(stepEnum.EMAIL);
    setEmailError('');
  };

  return (
    <div className={style.page_wrapper}>
      <Helmet>
        <title>Login - DeTalks{''}</title>
      </Helmet>
      {isLoading ? <Loader /> : null}
      <div className={style.wrapper}>
        {step === stepEnum.EMAIL && (
          <>
            <Button
              startIcon={<img className={style.logo} src={googleIcon} alt="Google" />}
              className={style.login_button}
              style={{ marginTop: '-35px', marginBottom: '10px' }}
              variant="contained"
              onClick={signInGmail}>
              Continue with Google
            </Button>
            <div className={style.separator}>
              <span className={style.line} />
              <span className={style.or_text}>or</span>
              <span className={style.line} />
            </div>
            <form className={style.email_form} onSubmit={continueWithEmail} autoComplete="off">
              <TextField
                required
                type="email"
                id="email"
                label="Email"
                variant="outlined"
                value={email}
                style={{ width: '100%', marginTop: '15px' }}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError('');
                }}
                error={!!emailError}
                helperText={emailError}
              />
              <Button
                type="submit"
                startIcon={<EmailIcon style={{ marginLeft: '-10px', fontSize: '25px' }} />}
                className={style.login_button}
                variant="contained"
                style={{ marginTop: '20px' }}
                disabled={isLoading || !emailRegex.test(email.trim())}>
                Continue with Email
              </Button>
            </form>
          </>
        )}

        {step === stepEnum.SIGN_IN && (
          <>
            <LoginMail validateUser={validateUser} initialEmail={email} />
            <div className={style.separator}>
              <span className={style.line} />
              <span className={style.or_text}>or</span>
              <span className={style.line} />
            </div>
            <Button
              startIcon={<img className={style.logo} src={googleIcon} alt="Google" />}
              className={style.login_button}
              style={{ marginTop: '20px' }}
              variant="contained"
              onClick={signInGmail}>
              Continue with Google
            </Button>
            <p className={style.identifier}>
              <IconButton
                color="primary"
                type="button"
                aria-label="Change email"
                className={style.back}
                onClick={editEmail}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </p>
          </>
        )}
      </div>
    </div>
  );
});

export default LoginMethods;
